import * as Sentry from "@sentry/react";

// Use a global variable to track initialization across HMR
const SENTRY_INITIALIZED_KEY = '__sentry_initialized__';

export function initializeSentry() {
  // Prevent multiple initializations (including across HMR)
  if ((window as any)[SENTRY_INITIALIZED_KEY]) {
    return;
  }

  // Only initialize Sentry in production or if explicitly enabled
  if (import.meta.env.PROD || import.meta.env.VITE_SENTRY_ENABLED === 'true') {
    (window as any)[SENTRY_INITIALIZED_KEY] = true;
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.PROD ? 'production' : 'development',
      integrations: [
        Sentry.browserTracingIntegration(),
        // Add replay integration for session recordings
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],
      
      // Performance monitoring
      tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
      
      // Session replay sample rate
      replaysSessionSampleRate: 0.1, // 10% of sessions
      replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
      
      // Release tracking
      release: import.meta.env.VITE_APP_VERSION,
      
      // Error filtering
      beforeSend(event, hint) {
        // Don't send events if we're in development and Sentry is not explicitly enabled
        if (!import.meta.env.PROD && import.meta.env.VITE_SENTRY_ENABLED !== 'true') {
          return null;
        }
        
        // Filter out certain errors
        if (event.exception) {
          const error = hint.originalException;
          if (error && error instanceof Error) {
            // Skip network errors that are likely user-related
            if (error.message.includes('NetworkError') || 
                error.message.includes('Failed to fetch')) {
              return null;
            }
            
            // Skip cancelled requests
            if (error.name === 'AbortError') {
              return null;
            }
          }
        }
        
        return event;
      },
      
      // User context
      initialScope: {
        tags: {
          component: 'frontend',
        },
      },
    });
  }
}

// Helper to set user context
export function setSentryUser(user: { id: string | number; email?: string; displayName?: string }) {
  Sentry.setUser({
    id: String(user.id),
    email: user.email,
    username: user.displayName,
  });
}

// Helper to add breadcrumbs
export function addSentryBreadcrumb(message: string, category: string, level: 'info' | 'warning' | 'error' = 'info') {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    timestamp: Date.now() / 1000,
  });
}

// Helper to capture exceptions
export function captureException(error: Error, context?: Record<string, any>) {
  return Sentry.captureException(error, {
    extra: context,
  });
}

// Helper to capture messages
export function captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info', context?: Record<string, any>) {
  return Sentry.captureMessage(message, {
    level,
    extra: context,
  });
}

export { Sentry };